import { Injectable, inject } from "@angular/core";
import { firstValueFrom, from } from "rxjs";
import { concatMap, map, pairwise, startWith } from "rxjs/operators";

import { initialData } from "../../../environments/environment";
import { PLATFORM_ADAPTER } from "../../core/platform/platform.adapter";
import { classCom } from "../../plugins/opsctrl-foc/focMain";
import { AuthService } from "./auth.service";
import { ClientTokenClaims, ClientTokenService } from "./client-token.service";
import { WebAuthRedirector } from "./web-auth-redirector.service";
import { CrashRecoveryService } from "../crash-recovery/crash-recovery.service";
import { SettingsService } from "../settings/settings.service";
import { opsControlService } from "../ops-control/ops-control.service";
import { FlightTimerService } from "../flight-timer/flight-timer.service";

interface SessionBootstrapResponse {
  activeDutyId: string | null;
  activeDutyDesig: string | null;
  activePic: string | null;
  activeHomebaseIdent: string;
  aircraftRegistration: string | null;
  activeCrewIdent: string | null;
  lastFlightId: string | null;
  source: "duty-now" | "duty-upcoming" | "none";
}

export type BootstrapStatus = "applied" | "empty" | "stale" | "unauthorized" | "error";

export interface BootstrapResult {
  status: BootstrapStatus;
  body?: SessionBootstrapResponse;
  error?: Error;
}

const FALLBACK_PIN = "WEB";

/**
 * Reads the server's view of "what duty is active for this registration right
 * now" and applies it to classCom + Auth/CrashRecovery so the rest of the app
 * — flow-briefing, opsCtrl, fpp/fcc providers — sees the same identity the
 * iPad would after its loadDuty chain.
 *
 * **Lifecycle ownership** — this service is the sole driver of:
 *   - classCom.activeDutyId / activeDutyDesig / activePic / activeHomebaseIdent
 *   - classCom.aircraftRegistration
 *   - AuthService.lastLoginInformation_device (registration = JWT
 *     registrationId, pin = parsed from crew ident)
 *   - CrashRecovery lastFlightLoaded (cleared on logout/rotation only — a
 *     persisted-session cold-start preserves any prior flightId even if the
 *     server's current bootstrap doesn't know about it).
 *
 * **Token transitions** — the service subscribes to `tokenObs` itself and
 * serializes transitions through `concatMap`. The subscription is owned here
 * rather than in AppComponent so the per-token Promise can be recorded
 * synchronously, ahead of the async transition body — that's what makes
 * `readyForToken` see an in-flight transition immediately after `setToken`
 * returns, with no BehaviorSubject filter race.
 *
 * **Route-readiness** — `readyForToken(token)` resolves with the latest
 * `BootstrapResult` for that token. `webAuthGuard` awaits it before allowing
 * protected routes to activate; the login page awaits it before navigating.
 * Both surfaces therefore see a populated classCom on the destination's first
 * render rather than racing the async bootstrap.
 *
 * Web has no per-pilot PIN, so the server picks one duty per registration by
 * time window (see efb-app-server `pickActiveDuty`): now-window prefers the
 * duty that started most recently; upcoming prefers the soonest-starting
 * duty. `created DESC` is the tiebreaker.
 */
@Injectable({ providedIn: "root" })
export class WebSessionBootstrapService {
  private platform = inject(PLATFORM_ADAPTER);
  private auth = inject(AuthService);
  private clientToken = inject(ClientTokenService);
  private opsCtrlSer = inject(opsControlService);
  private crashRecovery = inject(CrashRecoveryService);
  private redirector = inject(WebAuthRedirector);
  private settings = inject(SettingsService);
  private flightTimer = inject(FlightTimerService);

  /**
   * Per-token Promise of the (in-flight or settled) bootstrap result. Populated
   * synchronously inside the concatMap projection so `readyForToken` finds the
   * matching transition as soon as `setToken` returns control. Entries persist
   * for the lifetime of the session (typical ≤ 5 token rotations per browser
   * session — the Map doesn't need cleanup).
   */
  private readonly inflight = new Map<string, Promise<BootstrapResult>>();

  constructor() {
    if (this.platform.isNative) return;

    this.clientToken.tokenObs
      .pipe(
        startWith(null),
        pairwise(),
        // Synchronously record the per-token Promise on emission, BEFORE
        // concatMap buffers the next inner. concatMap will NOT invoke its
        // projection while a prior inner is still in flight — if we put
        // `inflight.set` inside the projection, a rapid setToken(B) during
        // A's still-running handleTransition has nothing in the Map for B
        // when login/guard call `readyForToken(B)`. They get {status:'stale'}
        // and activate the route against A's classCom (or whatever resetIdentity
        // just blanked it to). Stamping in this `map` runs on the synchronous
        // RxJS delivery and is the only way to honour the readyForToken contract.
        map(([prev, next]) => {
          let resolveTransition!: (r: BootstrapResult) => void;
          const transition = new Promise<BootstrapResult>((resolve) => {
            resolveTransition = resolve;
          });
          if (next) this.inflight.set(next, transition);
          return { prev, next, resolveTransition };
        }),
        // Wrap handleTransition so a thrown rejection cannot escape the
        // concatMap inner Observable — that would error the outer
        // subscription and silently drop every future token transition.
        concatMap(({ prev, next, resolveTransition }) =>
          from(
            (async () => {
              try {
                resolveTransition(await this.handleTransition(prev, next));
              } catch (err) {
                console.error("[sessionBootstrap] handleTransition errored:", err);
                resolveTransition({ status: "error", error: err as Error });
              }
            })()
          )
        )
      )
      .subscribe({
        error: (err) => {
          // Belt-and-suspenders — the try/catch above should prevent reaching
          // here. If we do, the subscription is dead; log loudly so the
          // missing token-transition handling is visible in Sentry.
          console.error("[sessionBootstrap] subscription died:", err);
        },
      });
  }

  /**
   * Resolve with the BootstrapResult for `token`. The token-transition
   * pipeline records a per-token Promise synchronously when `tokenObs`
   * dispatches the emission, so this finds the matching transition as soon
   * as `setToken(token)` returns control. If the token has never been
   * observed (e.g. tokenObs hasn't dispatched yet — should not happen given
   * BehaviorSubject is synchronous), waits one microtask and re-checks.
   *
   * Returns `{status: 'stale'}` for tokens that were rotated past before
   * being recorded. Callers (login page, webAuthGuard) treat 'unauthorized'
   * as "bootstrap already routed us, don't double-navigate" and treat
   * 'error' as transient (proceed and let the destination handle empty
   * classCom).
   */
  async readyForToken(token: string): Promise<BootstrapResult> {
    const direct = this.inflight.get(token);
    if (direct) return direct;
    await Promise.resolve();
    return this.inflight.get(token) ?? { status: "stale" };
  }

  private async handleTransition(prev: string | null, next: string | null): Promise<BootstrapResult> {
    if (prev && (!next || prev !== next)) {
      await this.resetIdentity();
      await this.clearFlightState();
    }

    if (!next) {
      return { status: "empty" };
    }

    // Capture claims at start of transition. `clientToken.getClaims()` is
    // expiry-aware: if the JWT has expired between emission and dispatch,
    // it returns null AND clears the session. Treat that as 'stale' (the
    // tokenObs clear has already queued the next [token, null] transition).
    const claims = this.clientToken.getClaims();
    if (!claims) return { status: "stale" };

    try {
      return await this.run(next, claims);
    } catch (err) {
      console.warn("[sessionBootstrap] failed:", err);
      return { status: "error", error: err as Error };
    }
  }

  /**
   * Point the persisted device settings at the registration this session is
   * scoped to, so the side-menu header names the aircraft the user actually
   * logged in as. `resetIdentity` calls it with the compiled default to clear
   * the field. No-op when it already matches — `changeSettings` pushes
   * through a BehaviorSubject whose subscriber re-persists on every emission,
   * so an unconditional write would put a storage round-trip on every token
   * refresh.
   */
  private async syncSettingsRegistration(registrationId: string): Promise<void> {
    if (!registrationId) return;
    try {
      // MUST wait for storage to be merged in. `ClientTokenService` hydrates
      // its session synchronously from localStorage, so on a reload with a
      // persisted session this runs in the first microtasks — well before
      // `SettingsService`'s `readSettings()`, which is chained off
      // `platform.ready()`. Reading the BehaviorSubject then yields the
      // compiled defaults, and writing `{...defaults, registration}` would
      // persist those defaults over the user's stored language / base / map
      // preferences AND be reverted moments later, since `readSettings`
      // spreads `...loadedSettings` last.
      await this.settings.loaded;
      // Backed by a BehaviorSubject, so this resolves with the current value
      // rather than waiting for the next emission.
      const current = await firstValueFrom(this.settings.settings$);
      if (current.registration === registrationId) return;
      await this.settings.changeSetting("registration", registrationId);
    } catch (err) {
      // Cosmetic only — the menu header keeps the stale tail. Never fail the
      // bootstrap over it.
      console.error("[sessionBootstrap] syncSettingsRegistration failed", err);
    }
  }

  /**
   * Restore the identity the TOKEN carries, after `resetIdentity()` has
   * cleared the duty-derived state. Used on the no-duty path only.
   *
   * The scope is knowable without a duty — `claims.registrationId` names the
   * aircraft whether or not anyone is rostered on it — and three separate
   * guards read those two fields as "no session at all", so blanking them
   * doesn't degrade the UI, it switches it off:
   *
   *   - `checkDutyLimitsValid` returns on `aircraftRegistration === ""` and
   *     again on `registration.length < 4`.
   *   - The `opsControlService` recompute loop has the same
   *     `registration.length < 4` guard before it reaches
   *     `checkDutyLimitsValid` — though on web that loop never starts at all
   *     (it is `isNative`-gated), so only the two guards above bite here.
   *   - Because it returns before assigning `typeOfDuty`, the duty-ofp-limits
   *     page falls into its managed-crew branch instead of `adhoc_only` — so
   *     the ad-hoc duty UI, the one thing that can CREATE the missing duty,
   *     is unreachable exactly when it is needed. Signing from that state
   *     throws `crewmemberdataByDuty … dutyId === undefined`.
   *
   * Only the aircraft identity is restored. `activeDutyId`/`activePic`/
   * `activeDutyDesig` stay empty — there genuinely is no duty, and code that
   * checks those must keep seeing the truth.
   *
   * `pin` MUST be the web PIN, not blank. Signing goes through
   * `getSigningPin()` (= `ClientTokenService.getWebPin()` on web), but
   * `checkDutyLimitsValid` reads this persisted field directly and builds
   * `ident = pin + "-" + aircraftRegistration`. With a blank pin that ident is
   * `"-OEXAM"`, which matches nothing, so `valid` is false even for a duty the
   * user just signed — and the `!valid` branch force-navigates
   * `/flow-briefing` → `/duty-ofp-limits` whenever a crew row exists and the
   * registration is ≥4 chars. Both were previously unreachable here because
   * the registration was blank; restoring the registration without the pin
   * would trap the user in a bounce-back loop on the exact path this fix
   * enables (create ad-hoc duty → sign → return to briefing → bounced).
   */
  private async applyScopeIdentity(claims: ClientTokenClaims): Promise<void> {
    classCom.aircraftRegistration = claims.registrationId;
    // Also the persisted settings copy, which `resetIdentity` just blanked to
    // "NOT SET" — this branch runs mid-bootstrap, AFTER run() already pointed
    // settings at the token's registration, so without this the side-menu
    // header reads "NOT SET" for exactly the no-duty case (measured).
    await this.syncSettingsRegistration(claims.registrationId);
    try {
      await this.auth.setLastLoginInformation_device({
        pin: this.clientToken.getWebPin(),
        registration: claims.registrationId,
        user: "",
        jwt: "",
        timestamp: Date.now(),
      });
    } catch (err) {
      // Non-fatal: classCom is already set, so the in-memory guards pass. Only
      // the persisted copy (read after a reload) is missing.
      console.error("[sessionBootstrap] applyScopeIdentity: persist registration failed", err);
    }
  }

  /**
   * Clears the running flight timer and the loaded flight. They belong to the
   * session that started them: kept, the timer's stop would write that flight
   * under the next pilot's tail. Not part of resetIdentity, because the no-duty
   * path of run() calls that on a plain reload, and a reload must keep both for
   * crash recovery. Runs on a token end or change, and from the web login page,
   * because a session that expired while the tab was closed produces no token
   * change here and would otherwise hand its timer to the next login.
   */
  public async clearFlightState(): Promise<void> {
    await this.flightTimer.reset();
    try {
      await this.crashRecovery.setLastFlightLoaded({ flightId: "" });
    } catch (err) {
      console.error("[sessionBootstrap] clearFlightState: clear lastFlight failed", err);
    }
  }

  /**
   * Drops the web-owned identity state. Called from handleTransition on any
   * token→null or token-rotation transition, and from run() when the same
   * token finds no active duty. It leaves the flight timer and the loaded
   * flight alone; clearFlightState clears those. Concurrency-safe because
   * handleTransition is serialized by concatMap — there's no interleaving
   * with a later bootstrap's writes.
   *
   * NOTE: this blanks the aircraft registration too, which is right for a
   * session ending but wrong on the no-duty path — see `applyScopeIdentity`,
   * which that caller runs afterwards to put the token-derived scope back.
   */
  private async resetIdentity(): Promise<void> {
    // All classCom identity fields are typed `string`; `""` is the codebase's
    // "unset" sentinel (mirrors the native bypass at
    // fppProvider.seedDevBypassSession which writes "" for activeHomebase).
    classCom.activeDutyId = "";
    classCom.activeDutyDesig = "";
    classCom.activePic = "";
    classCom.activeHomebaseIdent = "";
    classCom.aircraftRegistration = "";
    this.opsCtrlSer.currMapServiceAircraft = null;
    // The persisted settings copy too — `classCom` is in-memory only, so
    // without this the side-menu header still names the previous aircraft
    // after logout, and `SentryContextService` keeps tagging every subsequent
    // error with it (`sentry-context.service.ts` subscribes to
    // `settings$.registration` → `Sentry.setUser`/`setTag`), mis-attributing
    // anonymous errors to the pilot who logged out. Restores the compiled
    // default ("NOT SET"), which is what a never-logged-in browser shows.
    // On a rotation the following `run()` immediately re-points it at the new
    // registration via `syncSettingsRegistration`.
    await this.syncSettingsRegistration(initialData.settings.registration);
    try {
      await this.auth.setLastLoginInformation_device({
        pin: "",
        registration: "",
        user: "",
        jwt: "",
        timestamp: 0,
      });
    } catch (err) {
      console.error("[sessionBootstrap] resetIdentity: clear lastLogin failed", err);
    }
  }

  private async run(token: string, claims: ClientTokenClaims): Promise<BootstrapResult> {
    // The side-menu header renders `generalSettings.registration`, which is
    // persisted DEVICE settings (`GeneralSettings1` in storage). On native an
    // operator sets it once on the Settings page and it matches the airframe
    // forever. On web the registration is chosen per login and no login path
    // wrote it — so the menu kept showing whichever tail was last persisted
    // (e.g. still "OEXAM" after logging in as OEXVP), or the "NOT SET"
    // default. Sync it from the token on every bootstrap, before the duty
    // branch, so it is right whether or not a duty exists.
    //
    // Not the only writer: the Settings page writes it (its select is
    // disabled, so only via config crosslink), and flow-briefing-auto writes
    // the *username* into the same field on the `targetPlatform == na` branch.
    // The last writer wins; bootstrap runs first, so a later flow-briefing
    // entry can still overwrite the header. Left alone — untangling that
    // field's two meanings is not this change's job.
    await this.syncSettingsRegistration(claims.registrationId);

    let res: Response;
    try {
      res = await fetch("/session/bootstrap", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: "{}",
      });
    } catch (err) {
      return { status: "error", error: err as Error };
    }
    if (res.status === 401) {
      this.redirector.handle401();
      return { status: "unauthorized" };
    }
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      return { status: "error", error: new Error(`HTTP ${res.status}: ${text}`) };
    }
    let body: SessionBootstrapResponse;
    try {
      body = (await res.json()) as SessionBootstrapResponse;
    } catch (err) {
      return {
        status: "error",
        error: new Error(`/session/bootstrap returned non-JSON ${res.status}: ${(err as Error).message}`),
      };
    }

    if (this.clientToken.getTokenSync() !== token) {
      console.debug("[sessionBootstrap] dropping stale response — token changed mid-flight");
      return { status: "stale", body };
    }

    if (!body.activeDutyId) {
      await this.resetIdentity();
      // Put the token-derived scope back. Without this the app reads as
      // "logged out" rather than "logged in with nothing rostered", and the
      // ad-hoc duty UI that would fix that is itself switched off.
      if (this.clientToken.getTokenSync() !== token) return { status: "stale", body };
      await this.applyScopeIdentity(claims);
      console.warn(
        `[sessionBootstrap] no active duty for registration ${claims.registrationId} — ` +
          "flow-briefing will render empty until a duty exists. Create an ad-hoc duty " +
          "in duty-ofp-limits, or wait for the roster."
      );
      return { status: "empty", body };
    }

    await this.applyIdentity(body, token, claims);
    return { status: "applied", body };
  }

  /**
   * Apply the bootstrap response to classCom + Auth/CrashRecovery. Re-checks
   * the active token between long awaits so a token rotation mid-sequence
   * doesn't persist old-identity writes.
   *
   * `claims` is captured at run() start rather than re-fetched here — a
   * mid-bootstrap JWT expiry would clear the session and `getClaims()` would
   * return null, leading to `setLastLoginInformation_device({registration:""})`
   * polluting persistent storage.
   */
  private async applyIdentity(body: SessionBootstrapResponse, token: string, claims: ClientTokenClaims): Promise<void> {
    classCom.activeDutyId = body.activeDutyId!;
    classCom.activeDutyDesig = body.activeDutyDesig ?? "";
    classCom.activePic = body.activePic ?? "";
    classCom.activeHomebaseIdent = body.activeHomebaseIdent;
    classCom.aircraftRegistration = body.aircraftRegistration ?? "";

    // Two related "silent miss" states:
    //   - activeCrewIdent is null → derivePinFromIdent falls back to "WEB",
    //     getDutyLimits misses, signed-duty header stays empty.
    //   - activeCrewIdent set but aircraftRegistration missing →
    //     derivePinFromIdent returns the full ident (no suffix to strip),
    //     getDutyLimits builds ident + "-" + "" and ALSO misses, with no
    //     pin-fallback warn to surface it.
    // Warn on both so operators can diagnose without grepping silent empty
    // cards. Sentry's browser SDK captures console.warn as breadcrumbs.
    if (!body.activeCrewIdent) {
      console.warn(
        `[sessionBootstrap] activeCrewIdent missing for activeDutyId=${body.activeDutyId} ` +
          `(aircraft=${body.aircraftRegistration ?? "?"}, source=${body.source}). ` +
          `Falling back to pin="${FALLBACK_PIN}". Likely cause: NULL dutyDesig, ` +
          `the flightCrewId join missed (stale re-sign), or a >5s duty/flightcrew skew. ` +
          `Signed-duty header will stay empty until the crew row is reconciled.`
      );
    } else if (!body.aircraftRegistration) {
      console.warn(
        `[sessionBootstrap] aircraftRegistration is null for activeDutyId=${body.activeDutyId} ` +
          `but activeCrewIdent="${body.activeCrewIdent}" — derivePinFromIdent cannot strip ` +
          `the aircraft suffix; getDutyLimits will miss with no further signal. The duty ` +
          `row has no bound aircraft (partial provisioning?). Signed-duty header will stay empty.`
      );
    }

    const pin = derivePinFromIdent(body.activeCrewIdent, body.aircraftRegistration);

    // Re-check the active token before each persistent write. concatMap
    // serializes the NEXT handleTransition, but the writes already in flight
    // for THIS one don't see that serialization — a rotation lands between
    // awaits and we'd otherwise persist stale-identity state for the new
    // session to find on next boot.
    if (this.clientToken.getTokenSync() !== token) return;
    await this.auth.setLastLoginInformation_device({
      pin,
      registration: claims.registrationId,
      user: body.activePic ?? "",
      jwt: "",
      timestamp: Date.now(),
    });

    if (this.clientToken.getTokenSync() !== token) return;
    if (body.lastFlightId) {
      await this.crashRecovery.setLastFlightLoaded({ flightId: body.lastFlightId });
    }
    // Else: PRESERVE the existing crashRecovery pointer. The bootstrap's
    // `lastFlightId=null` only means "no flight is joined to the picked duty
    // via the tolerance window" — it does NOT mean the operator's prior
    // manually-loaded flight is invalid. clearFlightState (on logout, rotation
    // or a web login) is the sole clear path; this branch deliberately no-ops so a transient
    // server-side miss can't wipe recoverable state.

    if (this.clientToken.getTokenSync() !== token) return;
    if (body.aircraftRegistration) {
      try {
        const acft = await this.opsCtrlSer.focMain.getAircraftData(body.aircraftRegistration);
        // Re-check AFTER the await — getAircraftData can be a multi-hop SQL
        // round-trip and the token may have rotated mid-fetch. Without this
        // post-await check, A's aircraft persists into B's session whenever
        // B's bootstrap has `aircraftRegistration=null` (B's applyIdentity
        // skips this branch entirely, so it never overwrites A's stale value).
        if (acft && this.clientToken.getTokenSync() === token) {
          this.opsCtrlSer.currMapServiceAircraft = acft;
        }
      } catch (err) {
        console.warn("[sessionBootstrap] aircraft data load failed:", err);
      }
    }

    if (this.clientToken.getTokenSync() !== token) return;
    if (body.source === "duty-now") {
      try {
        await this.opsCtrlSer.loadSignedDutyHeaderFast();
      } catch (err) {
        console.warn("[sessionBootstrap] loadSignedDutyHeaderFast failed:", err);
      }
    }

    console.debug(`[sessionBootstrap] applied dutyId=${body.activeDutyId} source=${body.source}`);
  }
}

/**
 * Recover the pin from the flightcrew row's ident. The schema stores
 * `ident = pin + "-" + aircraftRegistration`, written symmetrically by every
 * `authorizeDutyLimits` site in mobile.
 *
 * If the suffix doesn't match (very old rows pre-dating the convention, or
 * data the iPad didn't write), fall back to the full ident as the pin —
 * `getDutyLimits` will build `ident + "-" + reg` and miss, which surfaces as
 * a benign empty getDutyLimits result rather than a wrong-row pickup.
 */
function derivePinFromIdent(ident: string | null, aircraftRegistration: string | null): string {
  if (!ident) return FALLBACK_PIN;
  if (aircraftRegistration) {
    const suffix = "-" + aircraftRegistration;
    if (ident.endsWith(suffix)) {
      return ident.slice(0, -suffix.length);
    }
  }
  return ident;
}
